import { Avatar } from "antd";
import clsx from "clsx";
import { memo, useMemo } from "react";
import type { Customer } from "types/customer";
import "./UserCell.scss";

const DEFAULT_AVATAR =
  "https://209qlcc.bmdapp.store/static/img/avatar_default.4da808b3.svg";

export type UserCellProps = {
  name: string;
  email?: string;
  avatar?: string;
  defaultAvatar?: string;
  className?: string;
};

export type UserCellCustomerProps = {
  customer: Pick<Customer, "firstName" | "lastName" | "email" | "avatar">;
  className?: string;
};

const getInitial = (name: string) => {
  const trimmed = name.trim();
  if (!trimmed) {
    return "?";
  }

  return trimmed.charAt(0).toUpperCase();
};

export const UserCell = memo(
  ({
    name,
    email,
    avatar,
    defaultAvatar = DEFAULT_AVATAR,
    className,
  }: UserCellProps) => {
    const avatarSrc = avatar || defaultAvatar;
    const showInitialFallback = !avatar;
    const initial = useMemo(() => getInitial(name), [name]);

    return (
      <div className={clsx("tableUserCell", className)}>
        <Avatar
          className="tableUserCellAvatar"
          src={showInitialFallback ? undefined : avatarSrc}
          alt={name}
        >
          {showInitialFallback ? initial : null}
        </Avatar>

        <div className="tableUserCellContent">
          <span className="tableUserCellName">{name}</span>
          {email ? <p className="tableUserCellEmail">{email}</p> : null}
        </div>
      </div>
    );
  },
);

export const UserCellFromCustomer = memo(
  ({ customer, className }: UserCellCustomerProps) => {
    const fullName = [customer.firstName, customer.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();

    return (
      <UserCell
        className={className}
        name={fullName || "Unknown"}
        email={customer.email}
        avatar={customer.avatar}
      />
    );
  },
);

UserCell.displayName = "UserCell";
UserCellFromCustomer.displayName = "UserCellFromCustomer";
